import firebase from '../config/firebase';
import { PIPELINE_STEPS, SUBMISSION_PUBLISH_STATUSES, RENT_MONTHLY_COST_PRESETS } from '../config/constants';
import { getCountry, getPropertyListingMode, formatUsd } from '../data/countries';
import { parsePropertyTimestamp, findPropertyById } from './properties';
import { buildUrlForRoute } from './routing';
import apiClient from './api';
import { normalizeRaisingMode } from './raisingMode';

function normalizePipelineStatus(status) {
  const raw = (status || "listed").toLowerCase().trim();
  if (raw === "rejected") return "rejected";
  const aliases = {
    "price agreement": "price_agreement",
    "live on site": "live",
    review: "listed",
    pending: "listed"
  };
  const key = aliases[raw] || raw.replace(/\s+/g, "_");
  return PIPELINE_STEPS.find((p) => p.key === key)?.key || "listed";
}

function pipelineRequiresApprovalPin(fromStatus, toStatus) {
  return normalizePipelineStatus(toStatus) === "approved"
    && normalizePipelineStatus(fromStatus) === "price_agreement";
}

function submissionSearchHaystack(sub) {
  const statePart = (sub.location || "").includes(",")
    ? (sub.location || "").split(",").pop().trim()
    : "";
  const countryName = sub.country ? (getCountry(sub.country).name || "") : "";
  return [
    sub.title,
    sub.location,
    statePart,
    sub.country,
    countryName,
    sub.type,
    sub.agentName,
    sub.agentEmail,
    sub.agentCompany,
    sub.submitterEmail,
    sub.status,
    formatSubmissionStatusLabel(sub.status)
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
}

function submissionMatchesSearch(sub, query) {
  const q = (query || "").trim().toLowerCase();
  if (!q) return true;
  return submissionSearchHaystack(sub).includes(q);
}

/** Uploaded listing photos from API (images[] or legacy image field). */
function getSubmissionPhotoUrls(sub) {
  const fromArray = Array.isArray(sub?.images) ? sub.images.filter(Boolean) : [];
  if (fromArray.length) return fromArray;
  if (sub?.image && String(sub.image).trim()) return [String(sub.image).trim()];
  return [];
}

const getSubmissionListingMode = (sub) => (sub?.listingMode === "rent" ? "rent" : "sale");

const formatSubmissionListingModeLabel = (sub, { short = false } = {}) => {
  const mode = getSubmissionListingMode(sub);
  if (mode === "rent") return short ? "Rent" : "For rent";
  return short ? "Buy" : "For sale (buy shares)";
};

function classifySubmission(status) {
  const s = normalizePipelineStatus(status);
  if (s === "rejected") return "rejected";
  if (s === "approved" || s === "live") return "approved";
  return "pending";
}

function submissionSortTime(sub) {
  const v = sub.submittedAt;
  if (v?.toDate) return v.toDate().getTime();
  if (v?.seconds) return v.seconds * 1000;
  if (sub.submittedDate) return new Date(sub.submittedDate).getTime();
  return 0;
}

function formatSubmissionStatusLabel(status) {
  return (status || "listed").replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
}

function submissionStatusBadgeClass(status) {
  const bucket = classifySubmission(status);
  if (bucket === "approved") return "approved";
  if (bucket === "rejected") return "rejected";
  if (normalizePipelineStatus(status) === "listed") return "review";
  return "pending";
}

function submissionIsPublicOnSite(sub) {
  if (!sub) return false;
  if (sub.deletedAt || sub.unpublishedAt) return false;
  if (sub.visibleOnSite === false || sub.isActive === false) return false;
  return SUBMISSION_PUBLISH_STATUSES.includes(normalizePipelineStatus(sub.status)) && !!sub.publishedPropertyId;
}

/** Public property page URL when a submission is live on the main site. */
function getSubmissionLiveSiteUrl(sub) {
  if (!submissionIsPublicOnSite(sub)) return null;
  const id = sub.publishedPropertyId || sub.propertyId;
  if (id == null || id === "") return null;
  return buildUrlForRoute("home", id);
}

const findSubmissionForProperty = (property, submissions) => {
  if (!property || !Array.isArray(submissions)) return null;
  if (property.sourceSubmissionId) {
    const byId = submissions.find((s) => s.id === property.sourceSubmissionId);
    if (byId) return byId;
  }
  return submissions.find((s) => s.publishedPropertyId === property.id) || null;
};

function getPipelineIndex(status) {
  if (normalizePipelineStatus(status) === "rejected") return -1;
  return PIPELINE_STEPS.findIndex((p) => p.key === normalizePipelineStatus(status));
}

function getNextPipelineStatus(status) {
  const idx = getPipelineIndex(status);
  if (idx < 0 || idx >= PIPELINE_STEPS.length - 1) return null;
  return PIPELINE_STEPS[idx + 1].key;
}

function getPrevPipelineStatus(status) {
  const idx = getPipelineIndex(status);
  if (idx <= 0) return null;
  return PIPELINE_STEPS[idx - 1].key;
}

function getPipelineStepLabel(key) {
  return PIPELINE_STEPS.find((p) => p.key === key)?.label || key.replace(/_/g, " ");
}

const resolveShareStructureClient = ({ price, raisingMode, shareCount, totalShares, minInvestment }) => {
  const p = Number(price) || 0;
  const mode = normalizeRaisingMode(raisingMode);
  if (mode === "co_owners") {
    let shares = parseInt(totalShares ?? shareCount, 10);
    if (!shares || shares < 1) {
      const min = Math.max(1, Number(minInvestment) || 500);
      shares = p > 0 ? Math.max(100, Math.round(p / min)) : 100;
      return { raisingMode: "min_investment", shares, minInvestment: min, shareCount: shares };
    }
    shares = Math.max(1, shares);
    const min = p > 0 && shares > 0 ? Math.max(1, Math.round((p / shares) * 100) / 100) : 500;
    return { raisingMode: mode, shares, minInvestment: min, shareCount: shares };
  }
  const min = Math.max(1, Number(minInvestment) || 500);
  const shares = p > 0 ? Math.max(1, Math.round(p / min)) : 100;
  return { raisingMode: mode, shares, minInvestment: min, shareCount: shares };
};

const validateRaisingStructureClient = ({ price, raisingMode, shareCount, totalShares, minInvestment }) => {
  const p = Number(price) || 0;
  if (p <= 0) return { ok: false, message: "Enter the total property value before setting how you want to raise." };
  const mode = normalizeRaisingMode(raisingMode);
  if (mode === "co_owners") {
    const count = parseInt(totalShares ?? shareCount, 10);
    if (!count || count < 1) return { ok: false, message: "Enter the number of co-owners (at least 1)." };
    if (count > 100000) return { ok: false, message: "Number of co-owners is too large." };
    return { ok: true };
  }
  const min = Number(minInvestment);
  if (!min || min < 1) return { ok: false, message: "Enter a minimum investment of at least $1." };
  if (min > p) return { ok: false, message: "Minimum investment cannot exceed the total property price." };
  return { ok: true };
};

const getListerProposedRaising = (sub) => ({
  raisingMode: sub?.proposedRaisingMode ?? sub?.raisingMode,
  shareCount: sub?.proposedShareCount ?? sub?.shareCount,
  totalShares: sub?.proposedTotalShares ?? sub?.totalShares,
  minInvestment: sub?.proposedMinInvestment ?? sub?.minInvestment
});

const getApprovedRaising = (sub) => ({
  raisingMode: sub?.raisingMode,
  shareCount: sub?.shareCount,
  totalShares: sub?.totalShares,
  minInvestment: sub?.minInvestment
});

const formatRaisingStructureLine = (sub, raising, { label } = {}) => {
  const price = Number(sub?.price) || 0;
  if (!price) return null;
  const { shares, minInvestment } = resolveShareStructureClient({ price, ...raising });
  const perShare = shares > 0 ? price / shares : 0;
  const line = `${shares.toLocaleString()} shares · $${perShare.toLocaleString(undefined, { maximumFractionDigits: 0 })} each · min. $${Number(minInvestment).toLocaleString()}`;
  return label ? `${label}: ${line}` : line;
};

const formatListerProposedShareSummary = (sub) => {
  if (getSubmissionListingMode(sub) === "rent") return null;
  return formatRaisingStructureLine(sub, getListerProposedRaising(sub));
};

const formatShareStructureSummary = (sub) => {
  if (getSubmissionListingMode(sub) === "rent") return null;
  const line = formatRaisingStructureLine(sub, getApprovedRaising(sub));
  if (!line) return null;
  if (sub?.raisingStructureApproved === false) return `${line} (pending admin approval)`;
  if (sub?.raisingStructureApproved === true) return `${line} (admin approved)`;
  return line;
};

const saleListingNeedsRaisingApproval = (sub) =>
  getSubmissionListingMode(sub) === "sale" &&
  (sub?.raisingStructureApproved === false || sub?.proposedRaisingMode != null) &&
  sub?.raisingStructureApproved !== true;

const formatAdminReviewTimestamp = (val) => {
  const d = parsePropertyTimestamp(val);
  if (!d) return "—";
  return d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
};

const formatAdminReviewerLabel = (entry) => {
  if (!entry) return "Unknown admin";
  if (entry.adminName && entry.adminEmail) return `${entry.adminName} (${entry.adminEmail})`;
  return entry.adminName || entry.adminEmail || entry.adminUid || "Unknown admin";
};

const adminReviewActionLabel = (action) => {
  const labels = {
    raising_structure_approved: "Share structure approved",
    pipeline_status: "Pipeline step updated",
    published_live: "Published to main site",
    unpublished: "Removed from main site",
    platform_price_set: "Platform listing price set",
    kyc_approved: "KYC approved",
    kyc_rejected: "KYC rejected",
    kyc_reopened: "KYC re-opened for review",
    report_open: "Chat report re-opened",
    report_reviewing: "Chat report review started",
    report_resolved: "Chat report resolved",
    report_dismissed: "Chat report dismissed",
  };
  return labels[action] || (action ? String(action).replace(/_/g, " ") : "Admin action");
};

const buildAdminReviewEntries = (sub) => {
  const fromLog = Array.isArray(sub?.adminReviewLog) ? sub.adminReviewLog.map((e) => ({ ...e })) : [];
  if (fromLog.length) {
    return fromLog.sort((a, b) => {
      const ta = parsePropertyTimestamp(a?.at)?.getTime() || 0;
      const tb = parsePropertyTimestamp(b?.at)?.getTime() || 0;
      return tb - ta;
    });
  }
  const legacy = [];
  if (sub?.raisingStructureApproved) {
    legacy.push({
      action: "raising_structure_approved",
      at: sub.raisingStructureApprovedAt,
      adminUid: sub.raisingStructureApprovedBy,
      adminEmail: sub.raisingStructureApprovedByEmail,
      adminName: sub.raisingStructureApprovedByName
    });
  }
  if (sub?.publishedAt && (sub.publishedBy || sub.publishedByEmail)) {
    legacy.push({
      action: "published_live",
      at: sub.publishedAt,
      adminUid: sub.publishedBy,
      adminEmail: sub.publishedByEmail,
      adminName: sub.publishedByName
    });
  }
  return legacy.sort((a, b) => {
    const ta = parsePropertyTimestamp(a?.at)?.getTime() || 0;
    const tb = parsePropertyTimestamp(b?.at)?.getTime() || 0;
    return tb - ta;
  });
};

const adminReviewEntryDetail = (entry) => {
  if (!entry) return "";
  if (entry.action === "pipeline_status" && entry.toStatus) {
    const from = entry.fromStatus ? formatSubmissionStatusLabel(entry.fromStatus) : "—";
    const to = formatSubmissionStatusLabel(entry.toStatus);
    return `${from} → ${to}${entry.detail ? ` · ${entry.detail}` : ""}`;
  }
  if (entry.notes) return entry.notes;
  if (entry.resolutionType) {
    const preset = {
      no_violation: "No policy violation found",
      warning_issued: "Warning issued",
      account_blocked: "Account blocked for policy violation",
      insufficient_evidence: "Insufficient evidence",
      duplicate_report: "Duplicate or invalid report",
      other: "Other resolution",
    };
    const label = preset[entry.resolutionType] || entry.resolutionType.replace(/_/g, " ");
    return entry.blockReportedUser ? `${label} · User blocked` : label;
  }
  if (entry.status) return `Status set to ${entry.status}`;
  return entry.detail || "";
};

const getSubmissionListerAsk = (sub) => {
  const n = Number(sub?.listerAskPrice ?? sub?.listerPrice ?? sub?.price);
  return Number.isFinite(n) && n > 0 ? n : 0;
};

const getSubmissionInvestorPrice = (sub) => {
  const n = Number(sub?.investorListingPrice ?? sub?.platformPrice);
  if (Number.isFinite(n) && n > 0) return n;
  return getSubmissionListerAsk(sub);
};

const submissionHasPlatformMarkup = (sub) => {
  const ask = getSubmissionListerAsk(sub);
  const investor = getSubmissionInvestorPrice(sub);
  return ask > 0 && investor > ask;
};

/** Who deleted a soft-deleted submission (admin vs lister), for Deleted listings tab. */
function getSubmissionDeletionActor(sub) {
  const listerEmails = [sub?.submitterEmail, sub?.agentEmail]
    .map((e) => String(e || "").trim().toLowerCase())
    .filter(Boolean);
  const deleterEmail = String(sub?.deletedByDisplayEmail ?? sub?.deletedByEmail ?? "").trim();
  const deleterNorm = deleterEmail.toLowerCase();
  let role = String(sub?.deletedByRole || "").trim().toLowerCase();
  if (role !== "admin" && role !== "lister") {
    role =
      deleterNorm && listerEmails.length && listerEmails.includes(deleterNorm) ? "lister" : "admin";
  }
  return { role, email: deleterEmail || null };
}

const emptyRentCostFormFields = () =>
  RENT_MONTHLY_COST_PRESETS.reduce((acc, row) => {
    acc[`rentCost_${row.id}`] = "";
    return acc;
  }, { rentCost_otherLabel: "" });

const parseRentCostAmount = (value) => {
  const n = Number(String(value ?? "").replace(/,/g, "").trim());
  return Number.isFinite(n) && n > 0 ? n : 0;
};

const rentCostFormFromRecord = (record) => {
  const fields = emptyRentCostFormFields();
  const raw = record?.monthlyCosts?.items;
  if (!Array.isArray(raw)) return fields;
  raw.forEach((row) => {
    const id =
      String(row?.id || "").trim() ||
      RENT_MONTHLY_COST_PRESETS.find((p) => p.label === String(row?.label || "").trim())?.id;
    if (!id || fields[`rentCost_${id}`] === undefined) return;
    fields[`rentCost_${id}`] = String(row.amount ?? "");
    if (id === "other") {
      const presetLabel = RENT_MONTHLY_COST_PRESETS.find((p) => p.id === "other")?.label;
      const custom = String(row.label || "").trim();
      if (custom && custom !== presetLabel) fields.rentCost_otherLabel = custom;
    }
  });
  return fields;
};

const NEARBY_AMENITY_IDS = ['airport', 'train', 'bus', 'supermarket', 'highway'];

const emptyNearbyFormFields = () => {
  const fields = {};
  NEARBY_AMENITY_IDS.forEach((id) => {
    fields[`nearby_${id}`] = '';
  });
  return fields;
};

const nearbyFormFromRecord = (record) => {
  const nearby = record?.nearby || {};
  const fields = {};
  NEARBY_AMENITY_IDS.forEach((id) => {
    const val = String(nearby[id] || '').trim();
    fields[`nearby_${id}`] = val && val !== 'N/A' ? val : '';
  });
  return fields;
};

const buildNearbyApiBody = (form) => {
  const nearby = {};
  NEARBY_AMENITY_IDS.forEach((id) => {
    const val = String(form[`nearby_${id}`] || '').trim();
    if (val) nearby[id] = val;
  });
  return Object.keys(nearby).length ? { nearby } : {};
};

const getNearbyEntries = (property) => {
  const nearby = property?.nearby;
  if (!nearby || typeof nearby !== 'object') return [];
  return Object.entries(nearby).filter(([, val]) => {
    const text = String(val || '').trim();
    return text && text !== 'N/A';
  });
};

const submissionContentFormFromRecord = (sub) => ({
  title: sub?.title || '',
  location: sub?.location || '',
  country: sub?.country || 'US',
  type: sub?.type || 'Residential',
  price: sub?.listerPrice ?? sub?.price ?? '',
  description: sub?.description || '',
  yearBuilt: sub?.yearBuilt != null ? String(sub.yearBuilt) : '',
  sqft: sub?.sqft != null ? String(sub.sqft) : '',
  beds: sub?.beds != null ? String(sub.beds) : '',
  baths: sub?.baths != null ? String(sub.baths) : '',
  monthlyRent: sub?.monthlyRent != null ? String(sub.monthlyRent) : '',
  occupancy: sub?.occupancy != null ? String(sub.occupancy) : '',
  annualOperatingExpenses: sub?.annualOperatingExpenses != null ? String(sub.annualOperatingExpenses) : '',
  netOperatingIncome: sub?.netOperatingIncome != null ? String(sub.netOperatingIncome) : '',
  capRate: sub?.capRate != null ? String(sub.capRate) : '',
  projectedReturn: sub?.projectedReturn != null ? String(sub.projectedReturn) : '',
  renovationNeeded: sub?.renovationNeeded || 'no',
  renovationCost: sub?.renovationCost != null ? String(sub.renovationCost) : '',
  renovationDesc: sub?.renovationDesc || '',
  landBanking: sub?.landBanking || 'no',
  agentName: sub?.agentName || '',
  agentCompany: sub?.agentCompany || '',
  agentEmail: sub?.agentEmail || sub?.submitterEmail || '',
  agentPhone: sub?.agentPhone || '',
  ...rentCostFormFromRecord(sub),
  ...nearbyFormFromRecord(sub),
});

const buildSubmissionContentApiBody = (form, sub) => {
  const listingMode = getSubmissionListingMode(sub);
  return {
    title: String(form.title || '').trim(),
    location: String(form.location || '').trim(),
    country: form.country,
    type: form.type,
    description: String(form.description || '').trim(),
    price: parseFloat(form.price) || 0,
    listerPrice: parseFloat(form.price) || 0,
    yearBuilt: form.yearBuilt,
    sqft: form.sqft,
    beds: form.beds,
    baths: form.baths,
    monthlyRent: form.monthlyRent,
    occupancy: form.occupancy,
    annualOperatingExpenses: form.annualOperatingExpenses,
    renovationNeeded: form.renovationNeeded,
    renovationCost: form.renovationCost,
    renovationDesc: form.renovationDesc,
    landBanking: form.landBanking,
    agentName: String(form.agentName || '').trim(),
    agentCompany: String(form.agentCompany || '').trim(),
    agentEmail: String(form.agentEmail || '').trim(),
    agentPhone: String(form.agentPhone || '').trim(),
    ...buildNearbyApiBody(form),
    ...(listingMode === 'rent' ? buildRentMonthlyCostsApiBody(form) : {}),
    ...(listingMode === 'sale'
      ? {
          netOperatingIncome: form.netOperatingIncome,
          capRate: form.capRate,
          projectedReturn: form.projectedReturn,
        }
      : {}),
  };
};

const buildRentMonthlyCostsApiBody = (form) => {
  const items = RENT_MONTHLY_COST_PRESETS.map((preset) => {
    const amount = parseRentCostAmount(form[`rentCost_${preset.id}`]);
    if (amount <= 0) return null;
    let label = preset.label;
    if (preset.id === "other") {
      const custom = String(form.rentCost_otherLabel || "").trim();
      if (custom) label = custom;
    }
    return { id: preset.id, label, amount };
  }).filter(Boolean);
  return items.length ? { monthlyCosts: { items } } : {};
};

const getRentMonthlyCostItems = (record) => {
  const raw = record?.monthlyCosts?.items;
  if (!Array.isArray(raw)) return [];
  return raw
    .map((row) => ({
      label: String(row?.label || "").trim(),
      amount: parseRentCostAmount(row?.amount)
    }))
    .filter((row) => row.label && row.amount > 0);
};

const sumRentMonthlyCostItems = (items) =>
  (items || []).reduce((sum, row) => sum + (Number(row.amount) || 0), 0);

const getRentAllInMonthly = (property) => {
  const base = Number(property?.monthlyRent) || 0;
  const extras = sumRentMonthlyCostItems(getRentMonthlyCostItems(property));
  return base + extras;
};

async function sha256File(file) {
  const buf = await file.arrayBuffer();
  const digest = await crypto.subtle.digest("SHA-256", buf);
  return Array.from(new Uint8Array(digest)).map((b) => b.toString(16).padStart(2, "0")).join("");
}

/** Strip non-digits from numeric form fields (letters blocked). */
function filterNumericInput(value, { decimal = false } = {}) {
  let v = String(value ?? "");
  if (decimal) {
    v = v.replace(/[^\d.]/g, "");
    const dot = v.indexOf(".");
    if (dot !== -1) v = v.slice(0, dot + 1) + v.slice(dot + 1).replace(/\./g, "");
  } else {
    v = v.replace(/\D/g, "");
  }
  return v;
}

const LISTING_VIDEO_EXTENSIONS = new Set(['mp4', 'mov', 'avi', 'mkv', 'mpg', 'mpeg']);
const LISTING_VIDEO_MAX_BYTES = 100 * 1024 * 1024;

const isListingVideoFile = (file) => {
  if (!file) return false;
  const mime = String(file.type || '').toLowerCase();
  if (mime.startsWith('video/')) return true;
  const ext = String(file.name || '').split('.').pop()?.toLowerCase();
  return LISTING_VIDEO_EXTENSIONS.has(ext);
};

async function uploadListingPhotosViaFirebase(files) {
  const user = firebase.auth().currentUser;
  if (!user) throw new Error("Please sign in to upload photos.");
  const storage = firebase.storage();
  const urls = [];
  for (const file of files) {
    const safeName = (file.name || "photo.jpg").replace(/[^\w.\-]+/g, "_");
    const path = `submissions/${user.uid}/${Date.now()}-${Math.random().toString(36).slice(2, 9)}-${safeName}`;
    const snap = await storage.ref(path).put(file, { contentType: file.type || "image/jpeg" });
    urls.push(await snap.ref.getDownloadURL());
  }
  return urls;
}

async function uploadListingVideoViaFirebase(file) {
  const user = firebase.auth().currentUser;
  if (!user) throw new Error("Please sign in to upload videos.");
  if (!isListingVideoFile(file)) {
    throw new Error("Unsupported video format. Use MP4, MOV, AVI, MKV, or MPG.");
  }
  if (file.size > LISTING_VIDEO_MAX_BYTES) {
    throw new Error("Video must be 100 MB or smaller.");
  }
  const storage = firebase.storage();
  const safeName = (file.name || "property-video.mp4").replace(/[^\w.\-]+/g, "_");
  const path = `submissions/${user.uid}/videos/${Date.now()}-${safeName}`;
  const snap = await storage.ref(path).put(file, { contentType: file.type || "video/mp4" });
  return snap.ref.getDownloadURL();
}

/** Upload listing photos: Firebase Storage (browser) first, then API fallback. */
async function uploadProfilePhotoViaFirebase(file) {
  const user = firebase.auth().currentUser;
  if (!user) throw new Error("Please sign in to upload a profile photo.");
  const storage = firebase.storage();
  const safeName = (file.name || "profile.jpg").replace(/[^\w.\-]+/g, "_");
  const path = `avatars/${user.uid}/${Date.now()}-${safeName}`;
  const snap = await storage.ref(path).put(file, { contentType: file.type || "image/jpeg" });
  return snap.ref.getDownloadURL();
}

async function uploadKycDocument(file) {
  const user = firebase.auth().currentUser;
  if (!user) throw new Error("Please sign in to upload your ID.");
  const storage = firebase.storage();
  const safeName = (file.name || "id-document.jpg").replace(/[^\w.\-]+/g, "_");
  const path = `kyc/${user.uid}/${Date.now()}-${safeName}`;
  const snap = await storage.ref(path).put(file, { contentType: file.type || "image/jpeg" });
  return snap.ref.getDownloadURL();
}

/** Insights cover: browser upload under submissions/ (same rules as List Property), then API fallback. */
async function uploadInsightCoverImage(file) {
  const user = firebase.auth().currentUser;
  if (!user) throw new Error("Please sign in to upload images.");

  const storage = firebase.storage();
  const safeName = (file.name || "cover.jpg").replace(/[^\w.\-]+/g, "_");
  const storagePaths = [
    `submissions/${user.uid}/insight-${Date.now()}-${safeName}`,
    `insights/${user.uid}/${Date.now()}-${safeName}`
  ];

  let lastFbErr = null;
  for (const path of storagePaths) {
    try {
      const snap = await storage.ref(path).put(file, { contentType: file.type || "image/jpeg" });
      return snap.ref.getDownloadURL();
    } catch (fbErr) {
      lastFbErr = fbErr;
      console.warn("[upload] insight cover:", path, fbErr.message);
    }
  }

  try {
    const res = await apiClient.uploadSubmissionImages([file]);
    const urls = Array.isArray(res.data) ? res.data : [];
    if (urls[0]) return urls[0];
  } catch (apiErr) {
    const apiMsg = apiErr.message || "";
    if (/photo storage is not available|bucket does not exist/i.test(apiMsg)) {
      throw new Error(
        "Upload could not reach cloud storage. From the repo run: firebase deploy --only storage, then bash scripts/fix-cloud-run-storage.sh"
      );
    }
    throw apiErr;
  }

  const fbMsg = (lastFbErr?.message || "").toLowerCase();
  if (fbMsg.includes("unauthorized") || fbMsg.includes("permission")) {
    throw new Error("Photo upload denied. Deploy storage rules: firebase deploy --only storage");
  }
  throw lastFbErr || new Error("Cover image upload failed");
}

async function uploadListingPhotos(files) {
  if (firebase.auth().currentUser) {
    try {
      return await uploadListingPhotosViaFirebase(files);
    } catch (fbErr) {
      console.warn("[upload] Firebase Storage:", fbErr.message);
      const msg = (fbErr.message || "").toLowerCase();
      if (msg.includes("unauthorized") || msg.includes("permission") || msg.includes("permission_denied")) {
        throw new Error("Photo upload denied. Ask your admin to deploy Firebase Storage rules (see storage.rules in repo).");
      }
    }
  }
  const res = await apiClient.uploadSubmissionImages(files);
  return res.data || [];
}

async function uploadListingVideo(file) {
  if (firebase.auth().currentUser) {
    try {
      return await uploadListingVideoViaFirebase(file);
    } catch (fbErr) {
      console.warn("[upload] Firebase Storage video:", fbErr.message);
      const msg = (fbErr.message || "").toLowerCase();
      if (msg.includes("unauthorized") || msg.includes("permission") || msg.includes("permission_denied")) {
        throw new Error("Video upload denied. Ask your admin to deploy Firebase Storage rules (see storage.rules in repo).");
      }
      if (!msg.includes("unsupported") && !msg.includes("100 mb")) {
        throw fbErr;
      }
    }
  }
  const res = await apiClient.uploadSubmissionVideo(file);
  return res.data || "";
}

/** Live listings with investors cannot be removed by the lister (admin may still remove). */
function listerCanRemoveSubmission(sub, properties = []) {
  const isLive =
    SUBMISSION_PUBLISH_STATUSES.includes(String(sub?.status || '').toLowerCase())
    && sub?.publishedPropertyId;
  if (!isLive) return { allowed: true };

  const prop = (Array.isArray(properties) ? properties : []).find(
    (p) => String(p.id) === String(sub.publishedPropertyId)
  );
  const sharesSold = Number(prop?.sharesSold) || 0;
  if (sharesSold > 0) {
    return {
      allowed: false,
      reason:
        'This listing has investors and cannot be removed. Contact CoOwn support if you need changes.'
    };
  }
  return { allowed: true };
}

export {
  normalizePipelineStatus,
  pipelineRequiresApprovalPin,
  submissionSearchHaystack,
  submissionMatchesSearch,
  getSubmissionPhotoUrls,
  getSubmissionListingMode,
  formatSubmissionListingModeLabel,
  classifySubmission,
  submissionSortTime,
  formatSubmissionStatusLabel,
  submissionStatusBadgeClass,
  submissionIsPublicOnSite,
  getSubmissionLiveSiteUrl,
  findSubmissionForProperty,
  getPipelineIndex,
  getNextPipelineStatus,
  getPrevPipelineStatus,
  getPipelineStepLabel,
  normalizeRaisingMode,
  resolveShareStructureClient,
  validateRaisingStructureClient,
  getListerProposedRaising,
  getApprovedRaising,
  formatRaisingStructureLine,
  formatListerProposedShareSummary,
  formatShareStructureSummary,
  saleListingNeedsRaisingApproval,
  formatAdminReviewTimestamp,
  formatAdminReviewerLabel,
  adminReviewActionLabel,
  buildAdminReviewEntries,
  adminReviewEntryDetail,
  getSubmissionListerAsk,
  getSubmissionInvestorPrice,
  submissionHasPlatformMarkup,
  getSubmissionDeletionActor,
  listerCanRemoveSubmission,
  emptyRentCostFormFields,
  emptyNearbyFormFields,
  nearbyFormFromRecord,
  buildNearbyApiBody,
  getNearbyEntries,
  parseRentCostAmount,
  rentCostFormFromRecord,
  buildRentMonthlyCostsApiBody,
  getRentMonthlyCostItems,
  sumRentMonthlyCostItems,
  getRentAllInMonthly,
  sha256File,
  filterNumericInput,
  uploadListingPhotosViaFirebase,
  uploadProfilePhotoViaFirebase,
  uploadKycDocument,
  uploadInsightCoverImage,
  uploadListingPhotos,
  uploadListingVideo,
  isListingVideoFile,
  submissionContentFormFromRecord,
  buildSubmissionContentApiBody,
};
